import { motion } from 'motion/react';
import { MessageCircle, Send, Heart, UserCheck, Users } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { supabase, isSupabaseConfigured } from '../backend';
import type { GuestbookMessage } from '../backend';

const backgroundImage = `${process.env.PUBLIC_URL || ''}/background/6.png`;

/** Llama a la Netlify Function para enviar notificación por correo (en segundo plano, no bloquea la UI). */
function notifyByEmail(payload: { type: 'message'; data: { name: string; message: string } } | { type: 'rsvp'; data: { guest_name: string; email?: string | null; attending: boolean; num_guests: number; message?: string | null } }) {
  const url = `${window.location.origin}/.netlify/functions/send-notification`;
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => { /* solo en producción existe la función */ });
}

export function GuestBook() {
  const [messages, setMessages] = useState<GuestbookMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // RSVP state
  const [guestName, setGuestName] = useState('');
  const [email, setEmail] = useState('');
  const [attending, setAttending] = useState<boolean | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpSuccess, setRsvpSuccess] = useState(false);
  const [rsvpError, setRsvpError] = useState<string | null>(null);

  const hasBackend = isSupabaseConfigured();

  useEffect(() => {
    if (hasBackend && supabase) {
      supabase
        .from('guestbook_messages')
        .select('id, name, message, created_at')
        .order('created_at', { ascending: false })
        .limit(50)
        .then(({ data, error }: { data: GuestbookMessage[] | null; error: Error | null }) => {
          setMessagesLoading(false);
          if (error) {
            console.error('Error cargando mensajes:', error);
            setMessages([]);
            return;
          }
          setMessages(data || []);
        });
    } else {
      setMessages([]);
      setMessagesLoading(false);
    }
  }, [hasBackend]);

  const handleSubmitMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    setSubmitError(null);
    setSubmitLoading(true);
    if (!hasBackend) {
      setSubmitLoading(false);
      setSubmitError('Configura Supabase para guardar los mensajes.');
      return;
    }
    if (supabase) {
      const { data, error } = await supabase
        .from('guestbook_messages')
        .insert({ name: name.trim(), message: message.trim() } as never)
        .select('id, name, message, created_at')
        .single();
      setSubmitLoading(false);
      if (error) {
        setSubmitError('No se pudo enviar el mensaje. Intenta de nuevo.');
        return;
      }
      if (data) {
        setMessages([data as GuestbookMessage, ...messages]);
        notifyByEmail({ type: 'message', data: { name: name.trim(), message: message.trim() } });
      }
    }
    setSubmitLoading(false);
    setName('');
    setMessage('');
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleSubmitRsvp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim() || attending === null) return;
    setRsvpError(null);
    setRsvpLoading(true);

    if (hasBackend && supabase) {
      const { error } = await supabase.from('rsvps').insert({
        guest_name: guestName.trim(),
        email: email.trim() || null,
        attending,
        num_guests: 0,
        message: null,
      } as never);
      setRsvpLoading(false);
      if (error) {
        setRsvpError('No se pudo enviar la confirmación. Intenta de nuevo.');
        return;
      }
      setRsvpSuccess(true);
      notifyByEmail({
        type: 'rsvp',
        data: {
          guest_name: guestName.trim(),
          email: email.trim() || null,
          attending,
          num_guests: 0,
          message: null,
        },
      });
      setGuestName('');
      setEmail('');
      setAttending(null);
      setTimeout(() => setRsvpSuccess(false), 5000);
    } else {
      setRsvpLoading(false);
      setRsvpError('Configura Supabase para guardar las confirmaciones (ver .env.example).');
    }
  };

  return (
    <section className="snap-section relative min-h-screen py-16 md:py-24 overflow-visible flex items-center justify-center">
      <div className="section-content w-full px-6 flex flex-col justify-center">
        <div
          className="section-bg-image absolute inset-0 z-0"
          style={{ backgroundImage: `url(${backgroundImage})` }}
          aria-hidden
        />
        <div className="absolute inset-0 z-[1]" style={{ backgroundColor: 'rgba(252, 235, 208, 0.85)' }} />
        <div className="relative z-10 w-full max-w-4xl mx-auto min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-editorial-title text-2xl md:text-3xl tracking-[0.3em] mb-4">
              LIBRO DE VISITAS
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto mb-4 break-words"
              style={{ color: 'var(--emerald)', opacity: 0.85, fontFamily: 'var(--font-serif)' }}
            >
              Déjanos tus buenos deseos y confirma tu asistencia
            </p>
            <div className="w-20 h-px mx-auto" style={{ backgroundColor: 'var(--chartreuse)' }} />
          </motion.div>

          <Tabs defaultValue="messages" className="w-full">
            <TabsList
              className="w-full max-w-md mx-auto mb-8 grid grid-cols-2 gap-2 rounded-2xl p-1.5 h-auto min-h-0 border-0 shadow-none items-stretch"
              style={{
                backgroundColor: 'var(--pale-lime)',
                border: '1px solid rgba(186, 159, 56, 0.25)',
              }}
            >
              <TabsTrigger
                value="messages"
                className="rounded-xl py-2.5 px-4 border-0 shadow-none outline-none focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[var(--butter)] data-[state=active]:shadow-none transition-colors inline-flex items-center justify-center gap-2 text-sm whitespace-nowrap min-h-0 overflow-hidden"
                style={{ color: 'var(--emerald)' }}
              >
                <MessageCircle className="w-4 h-4 shrink-0" />
                <span className="truncate">Deja un mensaje</span>
              </TabsTrigger>
              <TabsTrigger
                value="rsvp"
                className="rounded-xl py-2.5 px-4 border-0 shadow-none outline-none focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[var(--butter)] data-[state=active]:shadow-none transition-colors inline-flex items-center justify-center gap-2 text-sm whitespace-nowrap min-h-0 overflow-hidden"
                style={{ color: 'var(--emerald)' }}
              >
                <UserCheck className="w-4 h-4 shrink-0" />
                <span className="truncate">Confirmar asistencia</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="messages" className="mt-0">
              <div className="grid lg:grid-cols-2 gap-8 md:gap-10 min-w-0">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="rounded-xl p-6 md:p-8 border min-h-0 overflow-visible"
                  style={{
                    backgroundColor: 'var(--butter)',
                    borderColor: 'rgba(186, 159, 56, 0.3)',
                  }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center border shrink-0"
                      style={{
                        backgroundColor: 'var(--pale-lime)',
                        borderColor: 'var(--chartreuse)',
                      }}
                    >
                      <MessageCircle className="w-6 h-6" style={{ color: 'var(--emerald)' }} />
                    </div>
                    <h3 className="text-editorial-title text-lg tracking-[0.15em]" style={{ fontFamily: 'var(--font-serif)' }}>
                      DEJA TU MENSAJE
                    </h3>
                  </div>

                  <form onSubmit={handleSubmitMessage} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-editorial-meta text-xs mb-2">
                        Tu nombre
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border bg-white/80 focus:outline-none focus:ring-1 transition-all"
                        style={{ borderColor: 'rgba(186, 159, 56, 0.4)' }}
                        placeholder="Escribe tu nombre..."
                        required
                        disabled={submitLoading}
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-editorial-meta text-xs mb-2">
                        Tu mensaje
                      </label>
                      <textarea
                        id="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={5}
                        className="w-full px-4 py-3 rounded-lg border bg-white/80 focus:outline-none focus:ring-1 transition-all resize-none"
                        style={{ borderColor: 'rgba(186, 159, 56, 0.4)' }}
                        placeholder="Comparte tus deseos para los novios..."
                        required
                        disabled={submitLoading}
                      />
                    </div>
                    {submitError && (
                      <p className="text-sm" style={{ color: 'var(--destructive, #d4183d)' }}>{submitError}</p>
                    )}
                    <motion.button
                      type="submit"
                      disabled={submitLoading}
                      whileHover={!submitLoading ? { scale: 1.01 } : {}}
                      whileTap={!submitLoading ? { scale: 0.99 } : {}}
                      className="w-full py-3 rounded-full border flex items-center justify-center gap-2 transition-colors hover:bg-[var(--pale-lime)]/40 disabled:opacity-60"
                      style={{
                        borderColor: 'var(--chartreuse)',
                        color: 'var(--emerald)',
                        fontFamily: 'var(--font-serif)',
                      }}
                    >
                      <Send className="w-5 h-5" />
                      {submitLoading ? 'Enviando...' : 'Enviar mensaje'}
                    </motion.button>
                    {submitted && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 rounded-lg p-3"
                        style={{ backgroundColor: 'var(--pale-lime)', color: 'var(--emerald)' }}
                      >
                        <Heart className="w-5 h-5" style={{ color: 'var(--chartreuse)' }} />
                        <span className="text-sm">¡Gracias por tu mensaje! 💕</span>
                      </motion.div>
                    )}
                  </form>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="rounded-xl p-6 border max-h-[500px] md:max-h-[600px] overflow-y-auto min-h-0"
                  style={{
                    backgroundColor: 'rgba(255, 255, 240, 0.7)',
                    borderColor: 'rgba(186, 159, 56, 0.25)',
                  }}
                >
                  <h3 className="text-editorial-title text-base tracking-[0.12em] mb-6 shrink-0" style={{ fontFamily: 'var(--font-serif)' }}>
                    MENSAJES RECIENTES
                  </h3>
                  {messagesLoading ? (
                    <p className="text-editorial-meta text-sm" style={{ opacity: 0.8 }}>Cargando mensajes...</p>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, index) => (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="rounded-lg p-4 border bg-white/60 min-w-0 overflow-visible"
                          style={{ borderColor: 'rgba(186, 159, 56, 0.2)' }}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2 min-w-0">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0"
                                style={{ backgroundColor: 'var(--chartreuse)', color: 'var(--butter)' }}
                              >
                                {msg.name.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium truncate" style={{ color: 'var(--emerald)' }}>{msg.name}</span>
                            </div>
                            <span className="text-editorial-meta text-xs shrink-0" style={{ opacity: 0.7 }}>
                              {new Date(msg.created_at).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <p className="text-sm leading-relaxed break-words overflow-visible" style={{ color: 'var(--emerald)', opacity: 0.85 }}>
                            {msg.message}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  )}
                  {!messagesLoading && messages.length === 0 && (
                    <div className="text-center py-12">
                      <Heart className="w-12 h-12 mx-auto mb-4 opacity-50" style={{ color: 'var(--chartreuse)' }} />
                      <p className="text-editorial-meta text-sm" style={{ opacity: 0.8 }}>Sé el primero en dejar un mensaje</p>
                    </div>
                  )}
                </motion.div>
              </div>
            </TabsContent>

            <TabsContent value="rsvp" className="mt-0">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl p-6 md:p-8 border max-w-2xl mx-auto"
                style={{
                  backgroundColor: 'var(--butter)',
                  borderColor: 'rgba(186, 159, 56, 0.3)',
                }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center border shrink-0"
                    style={{
                      backgroundColor: 'var(--pale-lime)',
                      borderColor: 'var(--chartreuse)',
                    }}
                  >
                    <Users className="w-6 h-6" style={{ color: 'var(--emerald)' }} />
                  </div>
                  <h3 className="text-editorial-title text-lg tracking-[0.15em]" style={{ fontFamily: 'var(--font-serif)' }}>
                    CONFIRMAR ASISTENCIA
                  </h3>
                </div>

                <form onSubmit={handleSubmitRsvp} className="space-y-4">
                  <div>
                    <label htmlFor="guest_name" className="block text-editorial-meta text-xs mb-2">Nombre completo *</label>
                    <input
                      id="guest_name"
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border bg-white/80 focus:outline-none focus:ring-1 transition-all"
                      style={{ borderColor: 'rgba(186, 159, 56, 0.4)' }}
                      placeholder="Tu nombre o el de tu familia"
                      required
                      disabled={rsvpLoading}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-editorial-meta text-xs mb-2">Email (opcional)</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border bg-white/80 focus:outline-none focus:ring-1 transition-all"
                      style={{ borderColor: 'rgba(186, 159, 56, 0.4)' }}
                      placeholder="correo@ejemplo.com"
                      disabled={rsvpLoading}
                    />
                  </div>
                  <div>
                    <span className="block text-editorial-meta text-xs mb-2">¿Asistirás? *</span>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="attending"
                          checked={attending === true}
                          onChange={() => setAttending(true)}
                          disabled={rsvpLoading}
                          className="w-4 h-4"
                          style={{ accentColor: 'var(--chartreuse)' }}
                        />
                        <span style={{ color: 'var(--emerald)' }}>Sí, asistiré</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="attending"
                          checked={attending === false}
                          onChange={() => setAttending(false)}
                          disabled={rsvpLoading}
                          className="w-4 h-4"
                          style={{ accentColor: 'var(--chartreuse)' }}
                        />
                        <span style={{ color: 'var(--emerald)' }}>No podré asistir</span>
                      </label>
                    </div>
                  </div>
                  {rsvpError && (
                    <p className="text-sm" style={{ color: 'var(--destructive, #d4183d)' }}>{rsvpError}</p>
                  )}
                  {rsvpSuccess && (
                    <div className="flex items-center gap-2 rounded-lg p-3" style={{ backgroundColor: 'var(--pale-lime)', color: 'var(--emerald)' }}>
                      <Heart className="w-5 h-5" style={{ color: 'var(--chartreuse)' }} />
                      <span className="text-sm">¡Gracias! Tu confirmación se ha guardado correctamente.</span>
                    </div>
                  )}
                  <motion.button
                    type="submit"
                    disabled={rsvpLoading}
                    whileHover={!rsvpLoading ? { scale: 1.01 } : {}}
                    whileTap={!rsvpLoading ? { scale: 0.99 } : {}}
                    className="w-full py-3 rounded-full border flex items-center justify-center gap-2 transition-colors hover:bg-[var(--pale-lime)]/40 disabled:opacity-60"
                    style={{
                      borderColor: 'var(--chartreuse)',
                      color: 'var(--emerald)',
                      fontFamily: 'var(--font-serif)',
                    }}
                  >
                    <UserCheck className="w-5 h-5" />
                    {rsvpLoading ? 'Enviando...' : 'Confirmar'}
                  </motion.button>
                </form>
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}
